import { Module } from "vuex";
import studentsService from "@/services/studentsService";
import { Student, StudentQueryParams } from "@/interfaces/Student";
import { Platform } from "@/interfaces/Platform";

interface StudentsState {
  students: Student[];
  filters: object;
  selectedPlatform: Platform | null;
  loading: boolean;
  loadingStudentId: number | null;
  params: StudentQueryParams; // Query parameters for API
  meta: PaginationMeta; // Pagination metadata
  error: string | null; // Error handling
}

const studentsModule: Module<StudentsState, any> = {
  namespaced: true,

  // State: Add flexibility to the params
  state: (): StudentsState => ({
    students: [],
    filters: {},
    selectedPlatform: null,
    loading: false,
    loadingStudentId: null,
    params: {
      name: "", // Search query
      round: undefined, // Round filter
      status: undefined, // Status filter
      sort: "id",
      order: "asc",
      page: 1,
    },
    meta: {
      current_page: 0,
      last_page: 0,
      per_page: 0,
      total: 0,
      first_page_number: 1,
      last_page_number: 0,
      next_page_number: null,
      prev_page_number: null,
      pages: [],
    },
    error: null,
  }),

  // Mutations
  mutations: {
    setStudents(state, students: Student[]) {
      state.students = students;
    },
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setLoadingStudent(state, studentId: number | null) {
      state.loadingStudentId = studentId;
    },
    setParams(state, params: Partial<StudentsState["params"]>) {
      state.params = { ...state.params, ...params };
    },
    setFilters(state, filters: object) {
      state.filters = filters;
    },
    setPaginationMeta(state, meta: PaginationMeta) {
      state.meta = meta;
    },
    setError(state, error: string | null) {
      state.error = error;
    },
  },

  // Actions
  actions: {
    // Fetch students using the provided school URL
    async fetchStudents({ commit, state }, { url }) {
      commit("setLoading", true);
      try {
        const response = await studentsService.getStudents(url, state.params);
        commit("setStudents", response.data);
        commit("setFilters", response.data.filters || {});
        commit("setPaginationMeta", response.meta);
      } catch (error) {
        commit("setError", error || "Failed to fetch students");
      } finally {
        commit("setLoading", false);
      }
    },

    updateParams({ commit, dispatch }, { url, newParams }) {
      commit("setParams", newParams);
      dispatch("fetchStudents", { url }); // Pass URL when updating params
    },
  },

  // Getters: Add support for accessing new states
  getters: {
    allStudents: (state) => state.students,
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    errorMessage: (state) => state.error,
    loadingStudentId: (state) => state.loadingStudentId,
    filters: (state) => state.filters,
    params: (state) => state.params, // Expose params for debugging
  },
};

export default studentsModule;
